import React, { useState, useContext } from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  IconButton,
  Tooltip,
} from "@mui/material";
import { getPlatformLogoURL } from "#next/components/post/shared";
import { useUser } from "@auth0/nextjs-auth0/client";
import { excludeFromUserModal } from "#next/components/post/shared";
import { trackAction } from "#next/utils/trackUserActions";
import { Buttons } from "#next/components/post/Buttons";
import type { SearchResult } from "#next/utils/interfaces";
import { BLOCKED_IMAGE_URL } from "#next/utils/shared";
import { ReportableMedia } from "#next/components/post/ReportableMedia";
import { Subtitle } from "#next/components/post/Subtitle";
import { Stats } from "#next/components/post/Stats";
import { MediaModal } from "#next/components/modals/MediaModal";
import { FeedActionPrefix, PostContext } from "#next/components/post/contexts";

export const invisibleScrollStyle = {
  overflow: "scroll",
  "&::webkit-scrollbar": {
    display: "none",
  },
  "-ms-overflow-style": "none",
  "scrollbar-width": "none",
};

const TEXT_TRUNCATE_LIMIT = 900;

export const SinglePostFeed = ({
  posts,
  trackActionPrefix,
}: {
  posts: SearchResult[];
  trackActionPrefix: FeedActionPrefix;
}) => (
  <PostContext.Provider
    value={{
      wordLimit: TEXT_TRUNCATE_LIMIT,
      trackActionPrefix: trackActionPrefix,
      posts: posts,
      originalSearchResults: posts,
      onSelectPost: () => void 0,
      timeRange: "Last 180 days",
      textHighlights: [],
      translateAll: false,
      blurMedia: false,
      hideButtons: false,
      enableUserPreview: true,
      displayRelevance: true,
    }}
  >
    {posts.map((post) => (
      <Box key={post.id} sx={{ marginTop: 1 }}>
        <SinglePostInner post={post} hideMedia={true} />
      </Box>
    ))}
  </PostContext.Provider>
);

export const SinglePost = ({
  post,
  trackActionPrefix,
  hideMedia,
}: {
  post: SearchResult;
  trackActionPrefix: FeedActionPrefix;
  hideMedia?: boolean;
}) => (
  <PostContext.Provider
    value={{
      wordLimit: TEXT_TRUNCATE_LIMIT,
      trackActionPrefix: trackActionPrefix,
      posts: [post],
      originalSearchResults: [post], // Keeps context happy
      onSelectPost: () => void 0,
      timeRange: "Last 180 days",
      textHighlights: [],
      translateAll: false,
      blurMedia: false,
      hideButtons: false,
      enableUserPreview: true,
      displayRelevance: true,
    }}
  >
    <SinglePostInner
      post={post}
      hideMedia={hideMedia ?? true}
      hideSinglePostButton={true}
    />
  </PostContext.Provider>
);

function SinglePostInner({
  post,
  hideSinglePostButton,
  hideMedia,
}: {
  post: SearchResult;
  hideSinglePostButton?: boolean;
  hideMedia: boolean;
}) {
  const { blurMedia, translateAll } = useContext(PostContext);

  const [showTranslation, setShowTranslation] = useState(translateAll);
  const [mediaModalIsOpen, setMediaModalIsOpen] = useState(false);
  const sourceFlagEmoji = getFlagEmoji(post.language_code);
  const englishFlagEmoji = getFlagEmoji("US");

  return (
    <Card
      variant={"outlined"}
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        cursor: "pointer",
        boxSizing: "border-box",
        transition: "transform 0.3s, box-shadow 0.3s, border-color 0.3s",
        overflow: "visible",
      }}
    >
      <CardHeader
        avatar={
          <Avatar
            // would be nice to show user avatar here if we have it
            src={getPlatformLogoURL(post.platform)}
            alt={post.platform}
            sx={{
              width: 48,
              height: 48,
            }}
          />
        }
        title={
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Username username={post.username} platform={post.platform} />
            {post.translation && (
              <Tooltip
                title={
                  showTranslation
                    ? "Show original text"
                    : "Show English translation"
                }
              >
                <IconButton
                  onClick={() => setShowTranslation(!showTranslation)}
                  sx={{
                    backgroundColor: showTranslation
                      ? (theme) => theme.palette.secondary.dark
                      : undefined,
                  }}
                >
                  {showTranslation ? sourceFlagEmoji : englishFlagEmoji}
                </IconButton>
              </Tooltip>
            )}
          </Box>
        }
        subheader={<Subtitle post={post} />}
      />
      <CardContent sx={{ display: "flex" }}>
        <CardContent
          dangerouslySetInnerHTML={{
            __html:
              post.translation && showTranslation
                ? post.translation
                : post.text,
          }}
          sx={{
            ...invisibleScrollStyle,
            maxHeight: "25vh",
            flexGrow: 1,
          }}
        />
        {!hideMedia && !post.mediaIsBlocked && post.media[0] && (
          <Box
            sx={{
              ...invisibleScrollStyle,
              maxWidth: "33%",
              minWidth: "33%",
              maxHeight: 400,
            }}
          >
            <ReportableMedia
              post={post}
              blurMedia={blurMedia}
              handleMediaClicked={() => {
                if (post.media[0]) {
                  trackAction(`Single Post > Image > Open`);
                  setMediaModalIsOpen(true);
                }
              }}
              // TODO: Mutating data here isn't awesome
              onMediaReported={() => {
                post.media = [BLOCKED_IMAGE_URL];
                post.mediaIsBlocked = true;
              }}
            />
          </Box>
        )}
      </CardContent>
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <CardActions>
          <Stats post={post} />
        </CardActions>
        <CardActions>
          <Buttons post={post} hideSinglePostButton={hideSinglePostButton} />
        </CardActions>
      </Box>
      {post?.media[0] && (
        <MediaModal
          mediaModalIsOpen={mediaModalIsOpen}
          setMediaModalIsOpen={setMediaModalIsOpen}
          mediaUrl={post.media[0]}
        />
      )}
    </Card>
  );
}

// from https://dev.to/jorik/country-code-to-flag-emoji-a21
function getFlagEmoji(countryCode: string) {
  const codePoints = countryCode
    .toUpperCase()
    .split("")
    .map((char) => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
}

function Username({
  username,
  platform,
}: {
  username: string | null;
  platform: string;
}) {
  const { user } = useUser();

  return username === null ? (
    <>(unknown user)</>
  ) : (
    <Button
      variant="text"
      style={{ fontSize: 12 }}
      disabled={!user || excludeFromUserModal.includes(username.toLowerCase())}
      href={`/user-search?username=${username}&platform=${platform}`}
      onClick={() => {
        trackAction(`Single Post > User Search > Open`);
      }}
    >
      @{username}
    </Button>
  );
}
